import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { IconButton, Typography } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Videocam,
  VideocamOff,
  Mic,
  MicOff,
  CallEnd,
  Refresh,
} from "@mui/icons-material";
import Peer from "simple-peer";

/**
 * WebRTC Video Stream Component
 *
 * A reusable React component for embedding WebRTC video streams.
 * Can act as both initiator and receiver of video streams.
 *
 * @param {Object} props
 * @param {boolean} props.initiator - Whether this peer initiates the connection
 * @param {Object} props.signalData - Signal data from remote peer (for receiver)
 * @param {Function} props.onSignal - Callback when local signal is ready (signal) => void
 * @param {Function} props.onStream - Callback when remote stream is received (stream) => void
 * @param {Function} props.onConnect - Callback when connection is established
 * @param {Function} props.onClose - Callback when connection is closed
 * @param {Function} props.onError - Callback when error occurs (error) => void
 * @param {boolean} props.localVideo - Whether to show local video (default: true)
 * @param {boolean} props.remoteVideo - Whether to show remote video (default: true)
 * @param {MediaStreamConstraints} props.constraints - Media constraints for getUserMedia
 * @param {Object} props.style - Custom styles for the container
 * @param {boolean} props.showControls - Whether to show video controls (default: true)
 * @param {boolean} props.autoConnect - Whether to auto-connect on mount (default: true)
 */
const WebRTCStream = ({
  initiator = false,
  signalData = null,
  onSignal,
  onStream,
  onConnect,
  onClose,
  onError,
  localVideo = true,
  remoteVideo = true,
  constraints = {
    video: { width: 1280, height: 720 },
    audio: true,
  },
  style = {},
  showControls = true,
  autoConnect = true,
}) => {
  const [peer, setPeer] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [error, setError] = useState(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);

  // Convert MUI spacing to pixels (MUI spacing unit = 8px)
  const spacingToPx = (spacing) =>
    typeof spacing === "number" ? spacing * 8 : spacing;

  // Convert boxShadow number to actual shadow value
  const getBoxShadow = (elevation) => {
    const shadows = {
      0: "none",
      1: "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
      2: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
      3: "0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)",
      4: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
      5: "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)",
    };
    return shadows[elevation] || shadows[1];
  };

  // Initialize local media stream
  const initLocalStream = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      return stream;
    } catch (err) {
      console.error("Error accessing media devices:", err);
      const errorMsg = `Failed to access camera/microphone: ${err.message}`;
      setError(errorMsg);
      if (onError) onError(err);
      throw err;
    }
  }, [constraints, onError]);

  // Create peer connection
  const createPeer = useCallback(
    async (stream) => {
      try {
        setIsConnecting(true);
        setError(null);

        const peerInstance = new Peer({
          initiator,
          trickle: true,
          stream,
          config: {
            iceServers: [
              { urls: "stun:stun.l.google.com:19302" },
              { urls: "stun:stun1.l.google.com:19302" },
              { urls: "stun:stun2.l.google.com:19302" },
              { urls: "stun:stun3.l.google.com:19302" },
              { urls: "stun:stun4.l.google.com:19302" },
            ],
          },
        });

        // Handle signal data (for connection establishment)
        peerInstance.on("signal", (data) => {
          console.log("Signal data generated:", data.type);
          if (onSignal) onSignal(data);
        });

        // Handle incoming stream
        peerInstance.on("stream", (stream) => {
          console.log("Remote stream received");
          setRemoteStream(stream);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = stream;
          }
          if (onStream) onStream(stream);
        });

        // Handle connection
        peerInstance.on("connect", () => {
          console.log("Peer connection established");
          setIsConnected(true);
          setIsConnecting(false);
          if (onConnect) onConnect();
        });

        // Handle close
        peerInstance.on("close", () => {
          console.log("Peer connection closed");
          setIsConnected(false);
          setIsConnecting(false);
          if (onClose) onClose();
        });

        // Handle errors
        peerInstance.on("error", (err) => {
          console.error("Peer error:", err);
          const errorMsg = `Connection error: ${err.message}`;
          setError(errorMsg);
          setIsConnecting(false);
          if (onError) onError(err);
        });

        setPeer(peerInstance);
        peerRef.current = peerInstance;

        return peerInstance;
      } catch (err) {
        console.error("Error creating peer:", err);
        setIsConnecting(false);
        setError(`Failed to create connection: ${err.message}`);
        if (onError) onError(err);
        throw err;
      }
    },
    [initiator, onSignal, onStream, onConnect, onClose, onError]
  );

  // Initialize connection
  const initConnection = useCallback(async () => {
    try {
      const stream = await initLocalStream();
      await createPeer(stream);
    } catch (err) {
      console.error("Failed to initialize connection:", err);
    }
  }, [initLocalStream, createPeer]);

  // Handle incoming signal data
  useEffect(() => {
    if (signalData && peerRef.current && !isConnected) {
      try {
        console.log("Signaling remote peer:", signalData.type);
        peerRef.current.signal(signalData);
      } catch (err) {
        console.error("Error signaling peer:", err);
        setError(`Signaling error: ${err.message}`);
        if (onError) onError(err);
      }
    }
  }, [signalData, isConnected, onError]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      initConnection();
    }

    return () => {
      // Cleanup on unmount
      if (peerRef.current) {
        peerRef.current.destroy();
      }
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  }, [localStream]);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  }, [localStream]);

  // End call
  const endCall = useCallback(() => {
    if (peerRef.current) {
      peerRef.current.destroy();
      setPeer(null);
      peerRef.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }
    setRemoteStream(null);
    setIsConnected(false);
    setIsConnecting(false);
    if (onClose) onClose();
  }, [localStream, onClose]);

  // Reconnect
  const reconnect = useCallback(() => {
    endCall();
    setTimeout(() => {
      initConnection();
    }, 500);
  }, [endCall, initConnection]);

  // Main container styles
  const containerStyle = useMemo(
    () => ({
      position: "relative",
      width: "100%",
      height: "100%",
      backgroundColor: "#000",
      borderRadius: spacingToPx(2), // 16px
      overflow: "hidden",
      ...style,
    }),
    [style]
  );

  return (
    <div style={containerStyle}>
      {/* Remote Video */}
      {remoteVideo && (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: remoteStream ? "block" : "none",
          }}
        />
      )}

      {/* Local Video (Picture-in-Picture) */}
      {localVideo && localStream && (
        <div
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 200,
            height: 150,
            borderRadius: spacingToPx(2), // 16px
            overflow: "hidden",
            border: "2px solid #fff",
            boxShadow: getBoxShadow(3),
            zIndex: 10,
          }}
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
      )}

      {/* Connection Status */}
      {!remoteStream && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: "#fff",
            zIndex: 5,
          }}
        >
          {isConnecting && (
            <>
              <CircularProgress color="inherit" size={60} />
              <Typography variant="body1" style={{ marginTop: spacingToPx(2) }}>
                Connecting...
              </Typography>
            </>
          )}
          {!isConnecting && !isConnected && !error && (
            <Typography variant="body1">Waiting for connection...</Typography>
          )}
          {error && (
            <>
              <Typography
                variant="body1"
                color="error"
                style={{ marginBottom: spacingToPx(2) }}
              >
                {error}
              </Typography>
              <IconButton
                onClick={reconnect}
                style={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                }}
              >
                <Refresh />
              </IconButton>
            </>
          )}
        </div>
      )}

      {/* Controls */}
      {showControls && isConnected && (
        <div
          style={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: spacingToPx(2), // 16px
            backgroundColor: "rgba(0,0,0,0.7)",
            borderRadius: spacingToPx(2), // 16px
            padding: spacingToPx(1), // 8px
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={toggleVideo}
            style={{
              color: "#fff",
              backgroundColor: videoEnabled
                ? "rgba(255,255,255,0.2)"
                : "rgba(255,0,0,0.6)",
            }}
          >
            {videoEnabled ? <Videocam /> : <VideocamOff />}
          </IconButton>
          <IconButton
            onClick={toggleAudio}
            style={{
              color: "#fff",
              backgroundColor: audioEnabled
                ? "rgba(255,255,255,0.2)"
                : "rgba(255,0,0,0.6)",
            }}
          >
            {audioEnabled ? <Mic /> : <MicOff />}
          </IconButton>
          <IconButton
            onClick={endCall}
            style={{ color: "#fff", backgroundColor: "rgba(255,0,0,0.8)" }}
          >
            <CallEnd />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default WebRTCStream;
